import React from 'react';
import { graphql, useStaticQuery, Link } from 'gatsby';
import Layout from '../components/Layout';
import { ResourceCardTypePill } from './../components/ResourceCard';
import SEO from '../components/Seo';
import Button from '../components/common/Button';

type featuredResource = {
  title: string;
};

type Resource = {
  id: string;
  title: string;
  body: {
    body: string;
  };
  featuredResources: featuredResource[];
  type: string;
  image: {
    fluid: {
      src: string;
    };
  };
  slug: string;
};

// Helper function to extract plain text from Contentful rich text
const extractPlainText = (richText: any, maxLength: number = 150): string => {
  if (!richText) return '';
  
  let text = '';
  
  if (typeof richText === 'string') {
    try {
      richText = JSON.parse(richText);
    } catch {
      return '';
    }
  }
  
  if (richText && richText.content && Array.isArray(richText.content)) {
    const extractText = (node: any): string => {
      if (node.nodeType === 'text') {
        return node.value || '';
      }
      if (node.content && Array.isArray(node.content)) {
        return node.content.map(extractText).join('');
      }
      return '';
    };
    
    text = richText.content.map(extractText).join(' ').trim();
  }
  
  if (text.length > maxLength) {
    text = text.substring(0, maxLength) + '...';
  }
  
  return text;
};

const Resources: React.FC = () => {
  const data = useStaticQuery(graphql`
    {
      allContentfulResource(
        filter: { node_locale: { eq: "en-US" } }
        sort: { fields: title, order: ASC }
      ) {
        edges {
          node {
            title
            body {
              body
            }
            image {
              fluid {
                src
              }
            }
            type
            slug
            featuredResources {
              title
            }
          }
        }
      }
    }
  `);

  const resources: Resource[] = data.allContentfulResource.edges.map(
    (e: any) => e.node
  );

  return (
    <Layout>
      <SEO title="Resources" />
      <div className="min-h-screen bg-background overflow-hidden sans-serif">
        <section className="py-20 magazine-bg">
          <div className="container mx-auto px-6">
            <div className="max-w-7xl mx-auto">
              {/* Section Title */}
              <div className="text-center mb-12">
                <h1 className="text-4xl lg:text-5xl font-bold text-primary mb-6 text-balance sans-serif">
                  Resources for Teaching and Learning
                </h1>
                <div className="w-24 h-1 bg-gradient-to-r from-primary to-primary/40 mx-auto mb-8 rounded-full"></div>
              </div>

              {/* Resources Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {resources.map((resource: Resource) => {
                  const previewText = extractPlainText(resource.body?.body);
                  return (
                    <Link
                      key={resource.id}
                      to={`/resources/${resource.slug}`}
                      className="magazine-card bg-white creative-shadow overflow-hidden block transition-all hover:scale-105"
                    >
                      {/* Image */}
                      {resource.image?.fluid?.src && (
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={resource.image.fluid.src}
                            alt={resource.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      
                      {/* Content */}
                      <div className="p-6">
                        <div className="mb-3">
                          <ResourceCardTypePill type={resource.type} />
                        </div>
                        <h3 className="text-xl font-bold text-primary mb-3 leading-tight">
                          {resource.title}
                        </h3>
                        {previewText && (
                          <p className="text-foreground/70 text-sm leading-relaxed mb-4 line-clamp-3">
                            {previewText}
                          </p>
                        )}
                        <div className="text-primary font-semibold text-sm">
                          Learn More →
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Resources;
